import { PayloadAction } from '@reduxjs/toolkit'
import { put, select, call, takeEvery, take, race, delay, Effect } from 'redux-saga/effects'
import { BackendEvent, MapCollaborationEvent } from 'features/common/api/backendEvents'
import { backendEvent, takeEveryBackendEvent } from 'features/common/state/commonSlice'
import { lockMap, takeOverMapAction, unlockMap } from 'features/maps/api/maps'

import { Map, MapLockStatus } from 'features/maps/types'
import {
  setCurrentMapLockStatus,
  setTakeOverResponseState,
  unlockMapRequest,
  updateCurrentMapLockStatus
} from './builderSlice'
import { createApiError } from 'features/common/api/apiError'
import { usersMeQuery } from 'features/users/api/userQueries'
import { queryClient } from 'queryClient'
import { TAKEOVER_TIMEOUT_SEC } from 'features/collaboration/types'
import { TakeOverResult } from 'features/collaboration/types'

// All collaborarive backend event handlers only apply to currentMap.id, so check it first.
function collaborationEventHandlerCommon(handler: (event: MapCollaborationEvent, map: Map) => Generator) {
  return function* (payload: PayloadAction<BackendEvent>) {
    try {
      const { payload: event } = payload

      const currentMap: Map | undefined = yield select((state) => state.maps.currentMap)
      if (event.payload?.id === currentMap?.id) {
        yield handler(event as MapCollaborationEvent, currentMap)
      }
    } catch (error) {
      console.error(error)
    }
  }
}

function* handleMapLocked(event: MapCollaborationEvent) {
  try {
    yield put(
      updateCurrentMapLockStatus({
        locked: true,
        userId: event.payload.actor?.id,
        email: event.payload.actor?.email,
        pictureUrl: event.payload.actor?.pictureUrl
      })
    )
    const myUserId = yield select((state) => state.users.me?.user_id)

    // this is when we're "other" window of user that canceled request, we need to
    // reset local state too
    if (event.payload.actor?.id !== myUserId) {
      yield put(setTakeOverResponseState({ active: false }))
    }
  } catch (error) {
    console.error('handleMapLocked', error)
  }
}

function* handleMapUnlocked(event: MapCollaborationEvent) {
  try {
    yield put(
      updateCurrentMapLockStatus({
        locked: false,
        userId: event.payload.actor?.id,
        email: event.payload.actor?.email,
        pictureUrl: event.payload.actor?.pictureUrl,
        unlockedAt: Date.now()
      })
    )
  } catch (error) {
    console.error('handleMapUnlocked', error)
  }
}

function* handleMapTakeOverReject() {
  try {
    // this is when we're "other" window of user that canceled request, we need to
    // reset local state too
    yield put(setTakeOverResponseState({ active: false }))
  } catch (error) {
    console.error('handleMapTakeOverReject', error)
  }
}

function* handleMapTakeOverRequest(event: MapCollaborationEvent) {
  try {
    const currentMapLockStatus: MapLockStatus = yield select((state) => state.builder.currentMapLockStatus)
    const { user_id: myUserId } = yield queryClient.fetchQuery(usersMeQuery)
    if (currentMapLockStatus?.locked && currentMapLockStatus?.userId === myUserId) {
      yield put(
        setTakeOverResponseState({
          active: true,
          userId: event.payload.actor?.id,
          email: event.payload.actor.email,
          requestedTime: Date.now()
        })
      )
    }
  } catch (error) {
    console.error('handleMapTakeOverRequest', error)
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* takeOverCollaborativeMap(): Generator<Effect, TakeOverResult, any> {
  try {
    const mapId: string | undefined = yield select((state) => state.maps.currentMap?.id)
    const myUserId: string | undefined = yield select((state) => state.users.me?.user_id)

    if (!mapId || !myUserId) {
      throw new Error('map not available or not collaborative')
    }

    yield put(updateCurrentMapLockStatus({ takingOver: true }))
    let shouldWaitForTakeover = true

    try {
      yield call(takeOverMapAction, { mapId, action: 'request' })
    } catch (error) {
      const apiError = createApiError(error)
      if (apiError.error.match(/This map is not locked/)) {
        // no need to wait, attempt lock immediately
        shouldWaitForTakeover = false
      } else {
        throw apiError
      }
    }

    const kickOtherUserDeadline = Date.now() + TAKEOVER_TIMEOUT_SEC * 1000
    let shouldUnlock = false
    if (shouldWaitForTakeover) {
      // wait with deadline for collaboration events on this map
      while (true) {
        const { eventAction, timeout: timedOut } = yield race({
          eventAction: take(backendEvent.type),
          timeout: delay(Math.max(kickOtherUserDeadline - Date.now(), 0))
        })

        if (timedOut) {
          shouldUnlock = true
          break
        }

        if (eventAction) {
          const event: BackendEvent = eventAction.payload
          if (event.type === 'MapTakeoverAccept') {
            if (event.payload.id === mapId) {
              shouldUnlock = true
              break
            }
          } else if (event.type === 'MapTakeoverReject') {
            if (event.payload.id === mapId) {
              // end of story, UI will show message
              return 'denied'
            }
          } else if (event.type === 'MapUnlocked') {
            if (event.payload.id === mapId) {
              // unlocked, that was our target, let's assume this was "accidental" approval
              break
            }
          } else if (event.type === 'MapLocked') {
            if (event.payload.id === mapId) {
              // someone else locked map, start takeOver beginning with new deadline
              if (event.payload.actor.id === myUserId) {
                // it was us, assume it is succeess
                return 'locked'
              } else {
                // it was someone else, we should have notification and allow user to restart scenario
                return 'conflict'
              }
            }
          }
        }
      }
    }
    if (shouldUnlock) {
      try {
        yield call(unlockMap, { mapId })
      } catch {
        // here, we're racing with others attempting to unlock
        // so, if someone unlocked, good for us, maybe we'll manage to acquire lock in next step
      }
    }

    // map is unlocked, attempt lock
    try {
      yield call(lockMap, { mapId })
      yield put(updateCurrentMapLockStatus({ locked: true, userId: myUserId }))
      // we managed to get lock we're done
      return 'locked'
    } catch (error) {
      const apiError = createApiError(error)

      // TODO: maybe extract lock from metadata
      if (apiError.error.match(/Map is already locked/)) {
        // someone was faster, just repeat again whole procedure
        return 'conflict'
      } else {
        throw apiError
      }
    }
  } catch (error) {
    const apiError = createApiError(error)
    console.error('takeOverCollaborativeMap', error)
    throw new Error(`Take-over of map failed: ${apiError.error}`)
  } finally {
    yield put(updateCurrentMapLockStatus({ takingOver: false }))
  }
}

function* handleUnlockMapRequest(action: PayloadAction<string>) {
  try {
    const mapId = action.payload
    const currentMapLockStatus: MapLockStatus = yield select((state) => state.builder.currentMapLockStatus)
    const myUserId = yield select((state) => state.users.me?.user_id)

    if (currentMapLockStatus?.locked && currentMapLockStatus?.userId === myUserId) {
      yield put(setCurrentMapLockStatus({ locked: false }))
      yield call(unlockMap, { mapId })
    }
  } catch (error) {
    console.error('handleUnlockCurrentMapRequest', error)
  }
}

export default function* builderCollaborationSagas() {
  yield takeEveryBackendEvent('MapLocked', collaborationEventHandlerCommon(handleMapLocked))
  yield takeEveryBackendEvent('MapUnlocked', collaborationEventHandlerCommon(handleMapUnlocked))
  yield takeEveryBackendEvent('MapTakeoverRequest', collaborationEventHandlerCommon(handleMapTakeOverRequest))
  yield takeEveryBackendEvent('MapTakeoverReject', collaborationEventHandlerCommon(handleMapTakeOverReject))

  yield takeEvery(unlockMapRequest.type, handleUnlockMapRequest)
}
